import React from "react";
import connect from "react-redux/es/connect/connect";
import { withStyles, Typography, Button, Dialog, DialogActions, DialogContent, DialogContentText } from "@material-ui/core";
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import Checkbox from '@material-ui/core/Checkbox'
import TextField from '@material-ui/core/TextField'
import moment from 'moment-timezone'
import WarningIcon from '@material-ui/icons/Warning';
import DialogTitle from 'common/components/dialog/DialogTitle'
import styles from '../style';
import { studentsCleanupFetch, studentsCleanupBegin, studentsCleanupReset } from "redux/admin/Actions"
import RunningWorkerJobsDialog from 'apps/admin/components/WorkerJob/RunningWorkerJobsDialog'

const CLEANUP_REASON_LABELS = {
  entityDeactivated: 'Entity is deactivated',
  studentsSoftDeleted: 'Students are soft deleted',
};

class StudentsDataCleanup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedEntityIds: [],
      openDialog: false,
      email: '',
      password: '',
    };
  }

  componentDidMount() {
    this.props.studentsCleanupFetch();
  }

  componentDidUpdate(prevProps) {
    // drop selections for entities that no longer have anything to clean up (e.g. after a cleanup)
    if (this.props.entities !== prevProps.entities) {
      const entityIds = this.props.entities.map(entity => entity.id);
      const selectedEntityIds = this.state.selectedEntityIds.filter(id => entityIds.includes(id));
      if (selectedEntityIds.length !== this.state.selectedEntityIds.length) {
        this.setState({ selectedEntityIds });
      }
    }
  }

  componentWillUnmount() {
    // keep 'running' so the controls stay disabled if the user comes back before the cleanup request finishes
    if (this.props.status !== 'running') {
      this.props.studentsCleanupReset();
    }
  }

  handleSelectAllChange = (e) => {
    this.setState({ selectedEntityIds: e.target.checked ? this.props.entities.map(entity => entity.id) : [] });
  };

  handleSelectChange = (entityId) => (e) => {
    const { selectedEntityIds } = this.state;
    this.setState({
      selectedEntityIds: e.target.checked
        ? selectedEntityIds.concat(entityId)
        : selectedEntityIds.filter(id => id !== entityId)
    });
  };

  handleClickOpen = () => {
    this.setState({ openDialog: true });
  };

  handleClose = () => {
    this.setState({ openDialog: false, email: '', password: '' });
  };

  handleConfirm = () => {
    const { email, password, selectedEntityIds } = this.state;
    this.props.studentsCleanupBegin({ email, password, entityIds: selectedEntityIds });
    this.handleClose();
  };

  setValue(e, label) {
    this.setState({ [label]: e.target.value });
  }

  render() {
    const { classes, entities, isLoaded, status, message, lastTimeout } = this.props;
    const { selectedEntityIds, openDialog, email, password } = this.state;
    const isRunning = status === 'running';
    const allSelected = entities.length > 0 && selectedEntityIds.length === entities.length;
    const someSelected = selectedEntityIds.length > 0 && !allSelected;
    const selectedStudentCount = entities
      .filter(entity => selectedEntityIds.includes(entity.id))
      .reduce((total, entity) => total + entity.studentCount, 0);

    return (
      <div className={classes.root}>
        <RunningWorkerJobsDialog/>
        <div style={{ paddingLeft: 16 }}>
          <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginTop: 15, marginBottom: 15 }}>
            <WarningIcon style={{ marginRight: 15, color: 'orange' }} />
            Attention!
          </Typography>
          <Typography variant="subtitle1">Students of deactivated entities and soft deleted students of active entities will be permanently deleted from SchoolTrak,</Typography>
          <Typography variant="subtitle1">together with their in/out records, attendance records and schedules.</Typography>
          {lastTimeout ? (
            <Typography variant="subtitle1" color="error" style={{ marginTop: 15 }}>
              The students data cleanup started by {lastTimeout.startedBy} at {moment(lastTimeout.startedAt).format('MMM D, YYYY HH:mm')} timed
              out at {moment(lastTimeout.timedOutAt).format('HH:mm')} and was cancelled. No data was deleted.
            </Typography>
          ) : null}
        </div>

        <Table style={{ marginTop: 15 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={entities.length === 0 || isRunning}
                  onChange={this.handleSelectAllChange}
                />
              </TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="right">Students</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="subtitle1">{isLoaded ? 'There are no students to clean up.' : 'Loading...'}</Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {entities.map(entity => (
              <TableRow key={entity.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedEntityIds.includes(entity.id)}
                    disabled={isRunning}
                    onChange={this.handleSelectChange(entity.id)}
                  />
                </TableCell>
                <TableCell>{entity.displayName}</TableCell>
                <TableCell>{CLEANUP_REASON_LABELS[entity.cleanupReason]}</TableCell>
                <TableCell align="right">{entity.studentCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 15 }}>
          {isRunning ? <Typography variant="subtitle1" style={{ marginRight: 15 }}>Cleanup has started. Please wait a moment...</Typography> : null}
          {status === 'error' ? <Typography variant="subtitle1" color="error" style={{ marginRight: 15 }}>{message}</Typography> : null}
          <Button variant="contained" color="primary" disabled={selectedEntityIds.length === 0 || isRunning} onClick={this.handleClickOpen}>
            Cleanup
          </Button>
        </div>

        <Dialog
          open={openDialog}
          onClose={this.handleClose}
          aria-labelledby="students-cleanup-dialog-title"
        >
          <DialogTitle id="students-cleanup-dialog-title" onClose={this.handleClose}>
            Confirm students data cleanup
          </DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="subtitle1">To permanently delete <b>{selectedStudentCount}</b> students of <b>{selectedEntityIds.length}</b> selected entities</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="subtitle1">please enter your email and password</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="subtitle1"><b>Cleanup can not be undone!</b></Typography>
              </div>
              <TextField
                id="students-cleanup-email"
                label="Email"
                fullWidth
                margin="normal"
                variant="outlined"
                value={email}
                type="email"
                autoComplete="email"
                onChange={(e) => this.setValue(e, 'email')}
              />
              <TextField
                id="students-cleanup-pwd"
                label="Password"
                fullWidth
                type="password"
                autoComplete="current-password"
                margin="normal"
                variant="outlined"
                value={password}
                onChange={(e) => this.setValue(e, 'password')}
              />
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleClose} color="secondary">
              Cancel
            </Button>
            <Button disabled={email.length === 0 || password.length === 0} onClick={this.handleConfirm} color="primary" autoFocus>
              Cleanup
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
};

const mapStateToProps = state => {
  const { entities, isLoaded, status, message, lastTimeout } = state.admin.studentsCleanup;
  return { entities, isLoaded, status, message, lastTimeout };
};

const mapDispatchToProps = {
  studentsCleanupFetch, studentsCleanupBegin, studentsCleanupReset
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(StudentsDataCleanup));
